import { useQuery } from "@tanstack/react-query";
import { BarChart3, ChevronDown } from "lucide-react";
import { Link } from "react-router";
import { cn } from "@/lib/utils";
import { api } from "../../api";
import type { IssuesMeta } from "../../types";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { PRIORITY_LABELS } from "./meta";

export function InsightsDrawer({ meta }: { meta: IssuesMeta | undefined }) {
  const snapshot = useQuery({
    queryKey: ["issues-snapshot"],
    queryFn: api.analyticsSnapshot,
    refetchInterval: 30_000,
  });
  const rows = snapshot.data?.rows ?? [];
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  const byStatus = tally(rows, (row) => row.statusKey);
  const byType = tally(rows, (row) => row.typeKey);
  const byPriority = tally(rows, (row) => String(row.priority));

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <BarChart3 /> Insights
        </Button>
      </SheetTrigger>
      <SheetContent className="w-96 overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Issue insights</SheetTitle>
          <SheetDescription>{total} issues across the workspace</SheetDescription>
        </SheetHeader>
        <div className="space-y-6 px-4 pb-6">
          <InsightSection
            title="By status"
            entries={byStatus}
            total={total}
            labelFor={(key) => meta?.statuses.find((s) => s.key === key)?.name ?? key}
            colorFor={(key) => meta?.statuses.find((s) => s.key === key)?.color}
          />
          <InsightSection
            title="By type"
            entries={byType}
            total={total}
            labelFor={(key) => meta?.types.find((t) => t.key === key)?.name ?? key}
            colorFor={(key) => meta?.types.find((t) => t.key === key)?.color}
          />
          <InsightSection
            title="By priority"
            entries={byPriority}
            total={total}
            labelFor={(key) => PRIORITY_LABELS[Number(key)] ?? key}
          />
          <Button variant="outline" size="sm" asChild className="w-full">
            <Link to="/analytics">
              <BarChart3 /> Open the analytics dashboard
            </Link>
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function InsightSection({
  title,
  entries,
  total,
  labelFor,
  colorFor,
}: {
  title: string;
  entries: Array<[string, number]>;
  total: number;
  labelFor: (key: string) => string;
  colorFor?: (key: string) => string | undefined;
}) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1 text-sm font-medium">
        <ChevronDown className="size-3.5 text-muted-foreground" /> {title}
      </h3>
      <ul className="space-y-1.5">
        {entries.map(([key, count]) => (
          <li key={key} className="flex items-center gap-2 text-sm">
            <span className="w-28 truncate text-muted-foreground">{labelFor(key)}</span>
            <span className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <span
                className={cn("block h-full rounded-full", !colorFor?.(key) && "bg-primary/60")}
                style={{
                  width: `${total ? Math.max((count / total) * 100, 2) : 0}%`,
                  backgroundColor: colorFor?.(key),
                }}
              />
            </span>
            <span className="w-8 text-right tabular-nums">{count}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function tally<T>(rows: T[], keyOf: (row: T) => string): Array<[string, number]> {
  const map = new Map<string, number>();
  for (const row of rows) {
    const key = keyOf(row);
    map.set(key, (map.get(key) ?? 0) + (row as { count?: number }).count!);
  }
  return [...map.entries()].sort((a, b) => b[1] - a[1]);
}
